
import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Mail,
    Plus,
    Loader2,
    Edit,
    Trash2,
    FileText
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiCall } from "@/utils/api";
import EmailTemplateEditor from "@/components/email-templates/EmailTemplateEditor";

interface EmailTemplate {
    id?: number;
    name: string;
    subject: string;
    body: string;
    category?: string;
    created_at?: string;
}

const EmailTemplates: React.FC = () => {
    const { token } = useAuth();
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const data = await apiCall<EmailTemplate[]>(token, "/email-templates");
            setTemplates(data || []);
        } catch (error) {
            console.error("Failed to fetch templates:", error);
            toast.error("Impossible de charger les modèles");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleCreate = () => {
        setEditingTemplate(null);
        setIsEditorOpen(true);
    };

    const handleEdit = (template: EmailTemplate) => {
        setEditingTemplate(template);
        setIsEditorOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Êtes-vous sûr de vouloir supprimer ce modèle ?")) return;

        try {
            await apiCall(token, `/email-templates/${id}`, "DELETE");
            setTemplates(prev => prev.filter(t => t.id !== id));
            toast.success("Modèle supprimé");
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleSave = async (templateData: EmailTemplate) => {
        try {
            if (templateData.id) {
                // Update
                const updated = await apiCall<EmailTemplate>(
                    token,
                    `/email-templates/${templateData.id}`,
                    "PUT",
                    templateData
                );
                setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
                toast.success("Modèle mis à jour");
            } else {
                // Create
                const created = await apiCall<EmailTemplate>(
                    token,
                    "/email-templates",
                    "POST",
                    templateData
                );
                setTemplates(prev => [...prev, created]);
                toast.success("Modèle créé avec succès");
            }
        } catch (error) {
            console.error("Failed to save template:", error);
            toast.error("Erreur lors de l'enregistrement");
            throw error; // Re-throw to keep modal open if needed, though Editor handles saving state
        }
    };

    return (
        <div className="mx-auto px-4 py-4 space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                    <Mail className="h-32 w-32 rotate-12" />
                </div>
                <div className="z-10">
                    <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                        Modèles d'Emails
                    </h1>
                    <p className="text-slate-500 font-medium mt-1">
                        Gérez les templates utilisés pour vos communications automatiques.
                    </p>
                </div>
                <div className="z-10">
                    <Button
                        onClick={handleCreate}
                        className="rounded-xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                    >
                        <Plus className="h-5 w-5 mr-2" />
                        Nouveau Modèle
                    </Button>
                </div>
            </div>

            <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                            <FileText className="h-6 w-6" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-bold text-slate-900">Bibliothèque</CardTitle>
                            <CardDescription className="text-slate-500 font-medium">Liste de tous vos modèles d'emails disponibles.</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
                            <p className="text-slate-500 font-bold">Chargement des modèles...</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50/50 hover:bg-slate-50/50 border-slate-100">
                                    <TableHead className="w-[200px] font-bold text-slate-500 uppercase text-[10px] tracking-widest pl-6">Nom</TableHead>
                                    <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest">Sujet</TableHead>
                                    <TableHead className="font-bold text-slate-500 uppercase text-[10px] tracking-widest">Catégorie</TableHead>
                                    <TableHead className="text-right pr-6 font-bold text-slate-500 uppercase text-[10px] tracking-widest">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="h-32 text-center text-slate-400 font-bold italic">
                                            Aucun modèle trouvé. Créez votre premier template !
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    templates.map((template) => (
                                        <TableRow key={template.id} className="border-slate-50 hover:bg-slate-50/40 transition-colors group">
                                            <TableCell className="font-bold text-slate-900 pl-6">
                                                {template.name}
                                            </TableCell>
                                            <TableCell className="text-slate-600 font-medium max-w-[300px] truncate">
                                                {template.subject}
                                            </TableCell>
                                            <TableCell>
                                                {template.category && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                                        {template.category}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right pr-6">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleEdit(template)}
                                                        className="h-8 w-8 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(template.id!)}
                                                        className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <EmailTemplateEditor
                isOpen={isEditorOpen}
                onOpenChange={setIsEditorOpen}
                template={editingTemplate}
                onSave={handleSave}
            />
        </div>
    );
};

export default EmailTemplates;
